/**
 * Générateur — Connaître et utiliser la distributivité simple sur des exemples
 * numériques : k(a + b) = ka + kb et k(a − b) = ka − kb.
 * Attendu ID : 906 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 5)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « reconnaitre » — Ex.3 Relier les expressions égales
 *     Format : relier (4 paires). K, A grands (2 chiffres) par construction —
 *     le « sans effectuer de calculs » du doc est garanti : les produits sont
 *     infaisables de tête, seule la STRUCTURE permet d'apparier.
 *
 *   Carte « factoriser » — Ex.9 Calculs astucieux (factoriser)
 *     Format : texte_trou_saisie, étayage dégressif en rotation (4 gabarits,
 *     style feuille ligne à ligne). La somme/différence des coefficients tombe
 *     TOUJOURS sur 100 (ou 50) par construction → la factorisation mène à un
 *     ×100 (A5, conforme au doc).
 *
 *   Carte « developper » — Ex.11 Calcule astucieusement (développer)
 *     Formats : texte_trou_saisie (étapes), qcm_unique (rotation :
 *     décomposition astucieuse / résultat). Bases 100±δ et 1000±δ — les DEUX
 *     cas k(a+b) et k(a−b) tournent. Facteur toujours ENTIER.
 *
 *   Carte « mimi » — Ex.4 Les dépenses de Mimi (le sens concret)
 *     Formats : texte_trou_saisie (les DEUX chemins en parallèle dans la même
 *     phrase — l'égalité des chemins EST la distributivité), qcm_unique
 *     (question c fermée). Prix en euros ENTIERS.
 *
 *   Carte « aire » (hors quota) — Ex.5 + Ex.6 L'aire de deux façons (figures)
 *     Format : texte_trou_saisie avec figure SVG, rotation entre :
 *       - cas k(a+b) : rectangle découpé en deux (Ex.5) ;
 *       - cas k(a−b) : carré + rectangle bleu (Ex.6).
 *     Les DEUX méthodes dans la même phrase (arbitrage utilisateur). Les
 *     dimensions figurent AUSSI dans le texte (accessibilité + solveur), et sont ENTIÈRES.
 *
 *   Carte « methode » — niveau 6 : distribuer, factoriser, ou calculer directement ?
 *     Format : qcm_unique. Une question sur trois porte sur un calcul que la
 *     distributivité N'AIDE PAS (la spec en demande entre un cinquième et un tiers).
 *
 * ─── Spécification (906.json), mise en conformité du 2026-09-23 ─────────────
 *   Registre NUMÉRIQUE exclusivement, aucune lettre. Un seul facteur devant une
 *   seule parenthèse à DEUX termes : les gabarits à trois termes ont été refaits.
 *   AUCUN nombre décimal ni relatif (exclusion explicite) : les facteurs 0,5, les
 *   facteurs décimaux du développement, les prix en centimes et les dimensions à
 *   une décimale des figures ont tous été remplacés par des entiers.
 *   Erreurs visées → mots-clés : distribuer_toujours (croire que distribuer est
 *   toujours plus court), poser_au_lieu_de_decomposer (ne pas reconnaître 101
 *   comme 100 + 1), methode_confondue, calcul_direct_au_lieu_de_factoriser ; les
 *   erreurs de distribution partielle et de signe sont servies par les
 *   distracteurs des cartes « developper ».
 *   Graduation → cartes : niveaux 1 et 2 → mimi, aire, reconnaitre ;
 *   niveaux 3 et 4 → developper ; niveau 5 → factoriser ; niveau 6 → methode.
 */

import {
    randomChoice, randInt,
    makeQcm, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 906;

const L_RECO = 'Reconnaître les expressions égales (sans calculer)';
const L_FACT = 'Calculs astucieux : factoriser';
const L_DEV  = 'Calculs astucieux : développer';
const L_MIMI = 'Les dépenses de la semaine (deux chemins)';
const L_METH  = 'Quelle méthode est la plus courte ?';
const L_AIRE = "L'aire de deux façons (figure)";

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Centimes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

/** Déduplique des choix de QCM (rotation par mélange). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...candidats.sort(() => Math.random() - 0.5), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Reconnaître les expressions égales
// ═══════════════════════════════════════════════════════════

const genReconnaitreRelier = () => {
    const K = randInt(61, 97);
    let A = randInt(31, 59);
    if (A === K) A += 1;
    const b = randInt(3, 9);
    const pairs = [
        { source: `${K} × (${A} − ${b})`, target: `${K} × ${A} − ${K} × ${b}` },
        { source: `${A} × ${K} − ${A} × ${b}`, target: `${A} × (${K} − ${b})` },
        { source: `${K} × (${A} + ${b})`, target: `${K} × ${A} + ${K} × ${b}` },
        { source: `${A} × ${K} + ${A} × ${b}`, target: `${A} × (${K} + ${b})` },
    ];
    const texte = '<p><strong>Sans effectuer de calculs</strong>, relie les expressions ' +
        'qui conduisent au même résultat.</p>';
    return makeRelier(pairs, texte, opts(L_RECO));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Factoriser (étayage dégressif)
// ═══════════════════════════════════════════════════════════

/**
 * Quatre gabarits d'étayage dégressif. La spécification borne la parenthèse à DEUX termes
 * (« un seul facteur devant une seule parenthèse à deux termes ») et exclut tout nombre décimal :
 * les anciens gabarits à trois termes et à facteur 0,5 sont remplacés. La somme ou la différence
 * des deux coefficients tombe toujours sur 100, pour que la factorisation mène à un × 100.
 */
const GABARITS_FACT = [
    (l) => {   // a. pré-structuré, différence : (100 + y) × k − y × k
        const y = randInt(3, 30);
        const k = randInt(12, 49);
        return `${l} = ${100 + y} × ${k} − ${y} × ${k}\n` +
            `${l} = ({{${100 + y}}} − {{${y}}}) × ${k}\n` +
            `${l} = {{100}} × ${k}\n` +
            `${l} = {{${100 * k}}}`;
    },
    (l) => {   // b. somme, facteur commun écrit des deux côtés : p × k + k × q, p + q = 100
        const q = randInt(11, 89);
        const k = randInt(12, 49);
        return `${l} = ${100 - q} × ${k} + ${k} × ${q}\n` +
            `${l} = ${k} × ({{${100 - q}}} + {{${q}}})\n` +
            `${l} = ${k} × {{100}}\n` +
            `${l} = {{${100 * k}}}`;
    },
    (l) => {   // c. étayage réduit : le facteur commun est à trouver
        const q = randInt(21, 79);
        const k = randInt(12, 49);
        return `${l} = ${k} × ${q} + ${k} × ${100 - q}\n` +
            `${l} = {{${k}}} × ({{${q}}} + {{${100 - q}}})\n` +
            `${l} = {{${100 * k}}}`;
    },
    (l) => {   // d. étayage minimal : une seule ligne intermédiaire
        const y = randInt(5, 60);
        const k = randInt(12, 49);
        return `${l} = ${100 + y} × ${k} − ${y} × ${k}\n` +
            `${l} = ({{${100 + y}}} − {{${y}}}) × {{${k}}}\n` +
            `${l} = {{${100 * k}}}`;
    },
];

/**
 * Les trous SANS ORDRE de chaque gabarit, par indice de trou dans la phrase.
 * Seules les SOMMES en ont : l'élève qui échange les deux termes d'une somme écrit
 * encore quelque chose de vrai, celui qui échange les deux termes d'une DIFFÉRENCE
 * change la valeur (arbitrage prof 2026-09-24).
 */
const FACT_SANS_ORDRE = [
    [],           // a. différence : (100 + y) − y, l'ordre porte le sens
    [[0, 1]],     // b. somme dans la parenthèse
    [[1, 2]],     // c. somme dans la parenthèse ; le trou 0 est le facteur commun
    [],           // d. différence
];

const genFactoriserTrous = () => {
    const i = randInt(0, 3);
    const lettre = ['A', 'B', 'C', 'D'][i];
    const phrase = GABARITS_FACT[i](lettre);
    const texte = '<p>Effectue ce calcul <strong>de manière astucieuse</strong> : ' +
        'le facteur commun apparaît dans chaque produit. Complète :</p>';
    return makeTexteTrou(phrase, texte, {
        ...opts(L_FACT), mode: 'saisie', trousInterchangeables: FACT_SANS_ORDRE[i],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Développer
// ═══════════════════════════════════════════════════════════

/** k × (100±δ) ou k × (1000±δ) — les deux cas +/− tournent. */
const tirerDevelopper = () => {
    const grand = randomChoice([true, false]);
    const cent = grand ? 1000 : 100;
    const delta = grand ? randomChoice([1, 2, 10]) : randomChoice([1, 2, 3]);
    const plus = randomChoice([true, false]);
    const base = plus ? cent + delta : cent - delta;
    // Facteur toujours ENTIER : la spécification exclut les décimaux de cet objectif.
    const k10 = randInt(12, 29) * 10;
    const kF = dixToFr(k10);
    const res10 = k10 * base;                     // dixièmes, exact
    return { cent, delta, plus, base, kF, resF: dixToFr(res10), k10 };
};

const genDevelopperTrous = () => {
    const t = tirerDevelopper();
    const signe = t.plus ? '+' : '−';
    const phrase =
        `${t.kF} × ${t.base} = ${t.kF} × (${t.cent} ${signe} {{${t.delta}}})\n` +
        `${t.kF} × ${t.base} = ${t.kF} × ${t.cent} ${signe} ${t.kF} × {{${t.delta}}}\n` +
        `${t.kF} × ${t.base} = {{${t.resF}}}`;
    const texte = '<p>Calcule <strong>astucieusement</strong>, en décomposant. ' +
        'Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_DEV), mode: 'saisie' });
};

const genDevelopperQcm = () => {
    const t = tirerDevelopper();
    const signe = t.plus ? '+' : '−';
    const signeOpp = t.plus ? '−' : '+';
    if (randomChoice([true, false])) {
        // Choisir la décomposition astucieuse
        const bonne = `${t.kF} × (${t.cent} ${signe} ${t.delta})`;
        const pieges = [
            `${t.kF} × (${t.cent} ${signeOpp} ${t.delta})`,     // mauvais signe
            `${t.kF} × ${t.cent} ${signe} ${t.delta}`,          // n'a distribué qu'à moitié
            `(${t.kF} ${signe} ${t.delta}) × ${t.cent}`,        // décompose le mauvais facteur
        ];
        const texte = `<p>Pour calculer astucieusement <strong>${t.kF} × ${t.base}</strong>, ` +
            'quelle décomposition faut-il choisir ?</p>';
        return makeQcm([bonne, ...pieges], bonne, texte, opts(L_DEV));
    }
    // Le résultat
    const bonne = t.resF;
    const demiDistribue = dixToFr(t.k10 * t.cent + (t.plus ? 1 : -1) * t.delta * 10);
    const mauvaisSigne = dixToFr(t.k10 * (t.plus ? t.cent - t.delta : t.cent + t.delta));
    const distracteurs = dedupeChoix(bonne, [demiDistribue, mauvaisSigne],
        [dixToFr(t.k10 * t.base + 100), dixToFr(t.k10 * t.base - 100)]);
    const texte = `<p>Calcule astucieusement : <strong>${t.kF} × ${t.base}</strong></p>`;
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_DEV));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Les dépenses de la semaine (deux chemins)
// ═══════════════════════════════════════════════════════════

const CTX_MIMI = [
    { qui: 'Mimi', a1: 'une boisson', a1s: 'les boissons', a2: 'un sandwich', a2s: 'les sandwichs' },
    { qui: 'Léo', a1: 'un jus de fruits', a1s: 'les jus de fruits', a2: 'une salade', a2s: 'les salades' },
    { qui: 'Ana', a1: 'un lait chaud', a1s: 'les laits chauds', a2: 'une brioche', a2s: 'les brioches' },
];

/** Prix complémentaires à l'entier (0,90 + 2,10 = 3) → tout exact. */
/**
 * Les deux prix sont des ENTIERS d'euros : la spécification exclut les décimaux. Les nombres
 * restent ceux du niveau 1 (facteur de 2 à 9, termes de 1 à 9), ce qui laisse voir d'un coup
 * d'œil que les deux chemins donnent le même total.
 */
const tirerMimi = () => {
    const n = randInt(4, 6);
    const b = randInt(2, 5);
    const sec = randInt(2, 9 - b);
    return { n, bC: b, sC: sec, tC: b + sec, ctx: randomChoice(CTX_MIMI) };
};

const enonceMimi = ({ n, bC, sC, ctx }) =>
    `<p><strong>${n} jours par semaine</strong>, ${ctx.qui} achète ${ctx.a1} à ` +
    `<strong>${bC} €</strong> et ${ctx.a2} à <strong>${sC} €</strong>.</p>`;

const genMimiTrous = () => {
    const t = tirerMimi();
    const { n, bC, sC, tC, ctx } = t;
    const [nb, ns, nt, tF] = [n * bC, n * sC, n * tC, tC].map(String);
    const phrase =
        `Chemin 1 — Dépense pour ${ctx.a1s} : ${n} × ${bC} = {{${nb}}} €\n` +
        `Dépense pour ${ctx.a2s} : ${n} × ${sC} = {{${ns}}} €\n` +
        `Dépense totale : {{${nb}}} + {{${ns}}} = {{${nt}}} €\n` +
        `Chemin 2 — Dépense quotidienne : ${bC} + ${sC} = {{${tF}}} €\n` +
        `Dépense hebdomadaire : ${n} × {{${tF}}} = {{${nt}}} €`;
    const texte = enonceMimi(t) +
        '<p>Calcule la dépense hebdomadaire par les deux chemins. ' +
        'Complète :</p>';
    // La dépense totale est la SOMME des deux dépenses calculées juste au-dessus :
    // l'élève qui les additionne dans l'autre ordre trouve la même chose. Les trous 2
    // et 3 sont ces deux sommants ; le total (trou 4) reste jugé à sa place.
    return makeTexteTrou(phrase, texte, {
        ...opts(L_MIMI), mode: 'saisie', trousInterchangeables: [[2, 3]],
    });
};

const genMimiQcm = () => {
    const t = tirerMimi();
    const { n, bC, sC, tC } = t;
    const [bF, sF, ntF] = [bC, sC, n * tC].map(String);
    const bonne = `Les deux chemins donnent ${ntF} €, car ` +
        `${n} × ${bF} + ${n} × ${sF} = ${n} × (${bF} + ${sF})`;
    const pieges = [
        'C\'est un hasard si les deux chemins donnent le même résultat',
        'Le chemin 1 donne toujours un résultat plus grand que le chemin 2',
        `Les deux chemins donnent des résultats différents : il faut toujours utiliser le chemin 1`,
    ];
    const texte = enonceMimi(t) +
        '<p>On calcule la dépense hebdomadaire soit en séparant les deux achats (chemin 1), ' +
        'soit en partant de la dépense quotidienne (chemin 2). Que peut-on dire ?</p>';
    return makeQcm([bonne, ...pieges], bonne, texte, opts(L_MIMI));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — L'aire de deux façons (figures)
// ═══════════════════════════════════════════════════════════

const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

/** Ex.5 — rectangle découpé (cas k(a+b)) : a + b entier par construction. */
/**
 * Le rectangle découpé en deux parties. Les trois mesures sont des CENTIMÈTRES ENTIERS,
 * comme l'énoncé de la carte.
 *
 * ⚠️ Cette fonction parlait en DIXIÈMES de centimètre, du temps où la carte tirait des
 * décimaux. La mise en conformité avec la spécification a imposé des entiers sans
 * toucher au dessin : la figure annonçait « 0,3 cm » quand l'énoncé disait « 3 cm », et
 * se dessinait dix fois trop petite, si bien que les deux libellés du haut se
 * chevauchaient (signalé par le prof le 2026-09-24).
 */
const svgRectangleDecoupe = (a, b, h) => {
    const s = 18;                        // px par cm : a + b vaut 10 ou 20 cm
    const wa = a * s, wb = b * s, hh = h * s;
    const x0 = 60, y0 = 40;
    const W = Math.round(x0 + wa + wb + 40), H = Math.round(y0 + hh + 30);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        `<rect x="${x0}" y="${y0}" width="${wa + wb}" height="${hh}" fill="none" stroke="#1E3A8A" stroke-width="2"/>` +
        `<line x1="${x0 + wa}" y1="${y0}" x2="${x0 + wa}" y2="${y0 + hh}" stroke="#1E3A8A" stroke-width="1.4" stroke-dasharray="5 4"/>` +
        `<text x="${x0 + wa / 2}" y="${y0 - 10}" text-anchor="middle" font-size="14" fill="#0f172a">${a} cm</text>` +
        `<text x="${x0 + wa + wb / 2}" y="${y0 - 10}" text-anchor="middle" font-size="14" fill="#0f172a">${b} cm</text>` +
        `<text x="${x0 - 10}" y="${y0 + hh / 2 + 5}" text-anchor="end" font-size="14" fill="#0f172a">${h} cm</text>` +
        '</svg>';
};

/** Ex.6 — carré + rectangle bleu (cas k(a−b)). */
/** Le carré gris et le rectangle bleu accolés. Mesures en CENTIMÈTRES ENTIERS. */
const svgCarrePlusBleu = (L, c) => {
    const s = 18;                        // px par cm : L va de 6 à 12 cm
    const wc = c * s, w = L * s, hh = wc;
    const x0 = 64, y0 = 40;
    const W = Math.round(x0 + w + 40), H = Math.round(y0 + hh + 34);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        `<rect x="${x0}" y="${y0}" width="${wc}" height="${hh}" fill="#E2E8F0" stroke="#1E3A8A" stroke-width="2"/>` +
        `<rect x="${x0 + wc}" y="${y0}" width="${w - wc}" height="${hh}" fill="#60A5FA" stroke="#1E3A8A" stroke-width="2"/>` +
        `<text x="${x0 - 10}" y="${y0 + hh / 2 + 5}" text-anchor="end" font-size="14" fill="#0f172a">${c} cm</text>` +
        `<text x="${x0 + w / 2}" y="${y0 + hh + 22}" text-anchor="middle" font-size="14" fill="#0f172a">${L} cm</text>` +
        '</svg>';
};

const genAireTrous = () => {
    if (randomChoice([true, false])) {
        // Cas k(a + b) : rectangle découpé. Dimensions ENTIÈRES (la spec exclut les décimaux) ;
        // a + b tombe sur une dizaine ronde, ce qui rend le second chemin lisible.
        const T = randomChoice([10, 20]);
        const a = randInt(3, T - 3);
        const b = T - a;
        const h = randInt(2, 4);
        const [aF, bF] = [String(a), String(b)];
        const phrase =
            `Méthode 1 : ({{${aF}}} + {{${bF}}}) × ${h} = {{${T}}} × ${h} = {{${T * h}}} cm²\n` +
            `Méthode 2 : ${aF} × ${h} + ${bF} × ${h} = {{${a * h}}} + {{${b * h}}} = {{${T * h}}} cm²`;
        const texte = '<p>Le rectangle ci-dessous est découpé en deux parties : ' +
            `les largeurs mesurent <strong>${aF} cm</strong> et <strong>${bF} cm</strong>, ` +
            `la hauteur mesure <strong>${h} cm</strong>.</p>` +
            '<p>Calcule son aire totale <strong>de deux façons différentes</strong>.</p>';
        // Méthode 1, les deux largeurs additionnées dans la parenthèse (trous 0 et 1) :
        // leur ordre vient de la mise en page, pas du calcul.
        // Méthode 2 en revanche reste STRICTE : ses deux trous sont les aires des deux
        // morceaux du rectangle dessiné, et l'exercice demande justement de suivre
        // chaque morceau (arbitrage prof 2026-09-24).
        const payload = makeTexteTrou(phrase, texte, {
            ...opts(L_AIRE), mode: 'saisie', trousInterchangeables: [[0, 1]],
        });
        payload.elements_additionnels = imageElement(svgRectangleDecoupe(a, b, h));
        return payload;
    }
    // Cas k(L − c) : carré + rectangle bleu
    // Dimensions ENTIÈRES ici aussi.
    const L = randInt(6, 12);
    const c = randInt(2, L - 3);
    const cF = String(c);
    const resC = (L - c) * c;
    const phrase =
        `Méthode 1 : ({{${L}}} − {{${cF}}}) × ${cF} = {{${L - c}}} × ${cF} = {{${resC}}} cm²\n` +
        `Méthode 2 : ${L} × ${cF} − ${cF} × ${cF} = {{${L * c}}} − {{${c * c}}} = {{${resC}}} cm²`;
    const texte = '<p>La figure est formée d\'un carré (gris) et d\'un rectangle bleu : ' +
        `la longueur totale est <strong>${L} cm</strong> et le côté du carré mesure ` +
        `<strong>${cF} cm</strong>.</p>` +
        '<p>Calcule l\'aire du rectangle bleu <strong>de deux façons différentes</strong>. ' +
        'Complète :</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_AIRE), mode: 'saisie' });
    payload.elements_additionnels = imageElement(svgCarrePlusBleu(L, c));
    return payload;
};


// ═══════════════════════════════════════════════════════════
// Carte 6 — Quelle méthode est la plus courte ? (niveau 6)
// ═══════════════════════════════════════════════════════════

/**
 * Niveau 6 de la graduation : décider s'il vaut mieux distribuer, factoriser, ou calculer
 * directement. La spécification demande qu'entre un cinquième et un tiers des questions portent
 * sur un calcul que la distributivité NE simplifie PAS : c'est l'erreur visée « croire que
 * distribuer est toujours plus court ». Une question sur trois est donc de ce type.
 */
const tirerMethode = () => {
    const cas = randInt(1, 3);
    if (cas === 1) {
        // Un facteur proche d'une dizaine ronde, de 100 ou de 1 000 : distribuer est plus court.
        const cent = randomChoice([100, 1000]);
        const delta = randomChoice([1, 2, 3]);
        const plus = randomChoice([true, false]);
        const k = randInt(12, 49);
        const base = plus ? cent + delta : cent - delta;
        return {
            calcul: `${k} × ${base}`,
            bonne: `Distribuer : ${k} × ${cent} ${plus ? '+' : '−'} ${k} × ${delta}`,
            code: null,
            autres: [
                { valeur: `Poser la multiplication ${k} × ${base}`, code: 'poser_au_lieu_de_decomposer' },
                { valeur: 'Factoriser, il y a un facteur commun', code: 'methode_confondue' },
            ],
            niveau: 6,
        };
    }
    if (cas === 2) {
        // Deux produits de même facteur : factoriser est plus court.
        const k = randInt(12, 49);
        const q = randInt(21, 79);
        return {
            calcul: `${k} × ${q} + ${k} × ${100 - q}`,
            bonne: `Factoriser : ${k} × (${q} + ${100 - q})`,
            code: null,
            autres: [
                { valeur: `Calculer ${k} × ${q}, puis ${k} × ${100 - q}, puis additionner`, code: 'calcul_direct_au_lieu_de_factoriser' },
                { valeur: 'Distribuer, il y a une parenthèse', code: 'distribuer_toujours' },
            ],
            niveau: 6,
        };
    }
    // Un produit que rien ne simplifie : la distributivité allongerait le calcul.
    const a = randomChoice([23, 27, 34, 38, 43, 46, 52, 57]);
    const b = randomChoice([3, 4, 6, 7, 8, 9]);
    return {
        calcul: `${a} × ${b}`,
        bonne: `Calculer directement ${a} × ${b}`,
        code: null,
        autres: [
            { valeur: `Distribuer : ${a} × ${b} = ${a} × 10 − ${a} × ${10 - b}`, code: 'distribuer_toujours' },
            { valeur: 'Factoriser, il y a un facteur commun', code: 'methode_confondue' },
        ],
        niveau: 6,
        sansSimplification: true,
    };
};

const genMethodeQcm = () => {
    const t = tirerMethode();
    const texte = `<p>On veut calculer <strong>${t.calcul}</strong>.</p>`
        + '<p><strong>Quelle méthode est la plus courte ?</strong></p>';
    const payload = makeQcm([t.bonne, ...t.autres.map((a) => a.valeur)], t.bonne, texte, opts(L_METH));
    payload._meta.params = {
        niveau: 6, calcul: t.calcul, distracteurs: t.autres,
        ...(t.sansSimplification ? { sansSimplification: true } : {}),
    };
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    reconnaitre_relier:          genReconnaitreRelier,
    factoriser_texte_trou_saisie: genFactoriserTrous,
    developper_texte_trou_saisie: genDevelopperTrous,
    developper_qcm_unique:       genDevelopperQcm,
    mimi_texte_trou_saisie:      genMimiTrous,
    mimi_qcm_unique:             genMimiQcm,
    aire_texte_trou_saisie:      genAireTrous,
    methode_qcm_unique:          genMethodeQcm,
};

export const CATEGORIES = [
    {
        id: 'reconnaitre', label: L_RECO,
        formatsList: [{ key: 'reconnaitre_relier', label: 'Relier' }],
    },
    {
        id: 'factoriser', label: L_FACT,
        formatsList: [{ key: 'factoriser_texte_trou_saisie', label: 'Saisie trous' }],
    },
    {
        id: 'developper', label: L_DEV,
        formatsList: [
            { key: 'developper_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'developper_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'mimi', label: L_MIMI,
        formatsList: [
            { key: 'mimi_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'mimi_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'aire', label: L_AIRE,
        formatsList: [{ key: 'aire_texte_trou_saisie', label: 'Saisie trous' }],
    },
    {
        id: 'methode', label: L_METH,
        formatsList: [{ key: 'methode_qcm_unique', label: 'QCM' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    dixToFr, centToFr, dedupeChoix,
    GABARITS_FACT, tirerDevelopper, tirerMimi,
    svgRectangleDecoupe, svgCarrePlusBleu,
};
